<template>
  <div class="table-wrap no-wrap-css">
    <!-- Success and Error Message Slot -->
    <div v-if="showMessage" :class="['message', messageType]" class="message-container">
      <slot name="message">{{ message }}</slot>
    </div>

    <div class="parent-wrap">
      <div
        id="mySavedReportsContainer"
        ref="table-scroll"
        class="table-scroll my-saved-reports-container"
      >
        <div>
          <template v-if="!loading">
            <table class="tbl tbl-export" :class="css">
              <thead class="header-top">
                <tr>
                  <th
                    v-if="rowsCollapsible"
                    class="short-column sticky-column-header subRowCollapsible"
                  ></th>

                  <th
                    :ref="`${headers[0].name}_0`"
                    :class="[
                      'headers',
                      { 'sticky-column sticky-column-header': headers[0].isSticky },
                    ]"
                    @mouseover="
                      (el) => handleMouseOver(el, headers[0].name, `${headers[0].name}_0`)
                    "
                    @mouseleave="handleMouseLeave"
                  >
                    <div class="header-container">
                      <div class="header-row">
                        <span class="header-text">{{ headers[0].name }}</span>
                        <div @click="toggleFilter">
                          <font-awesome-icon
                            class="filter-icon"
                            :icon="['fas', 'magnifying-glass']"
                            :class="{ rotated: showFilter }"
                          />
                        </div>
                      </div>
                      <input
                        v-if="showFilter"
                        ref="savedReportsUIFilter"
                        v-model="filterText"
                        class="filter-input"
                        placeholder="Filter..."
                      />
                    </div>
                  </th>

                  <th
                    v-for="(header, i) in headersWithoutFirst"
                    :key="i"
                    :ref="`${header.name}_${i + 1}`"
                    :class="['headers', { 'sticky-column sticky-column-header': header.isSticky }]"
                    @mouseover="(el) => handleMouseOver(el, header.name, `${header.name}_${i + 1}`)"
                    @mouseleave="handleMouseLeave"
                  >
                    <span>{{ header.name }}</span>
                  </th>
                </tr>
              </thead>
              <tbody v-if="filteredRows.length" class="body-half-screen">
                <tr
                  v-for="(item, i) in filteredRows"
                  :id="i"
                  :key="i"
                  :class="[{ 'sub-row': !!item.subRow }, { highlightBackgroundParent: item.open }]"
                >
                  <td
                    :ref="`row_name_${i}`"
                    class="saved-reports-cell"
                    @mouseover="handleMouseOver($event, item.name, `row_name_${i}`)"
                    @mouseleave="handleMouseLeave"
                  >
                    <div class="tooltip-container">{{ item.name }}</div>
                  </td>
                  <td
                    :ref="`row_dateRange_${i}`"
                    @mouseover="handleMouseOver($event, item.dateRange, `row_dateRange_${i}`)"
                    @mouseleave="handleMouseLeave"
                  >
                    <div class="tooltip-container">{{ item.dateRange }}</div>
                  </td>
                  <td
                    :ref="`row_createdAt_${i}`"
                    @mouseover="handleMouseOver($event, item.createdAt, `row_createdAt_${i}`)"
                    @mouseleave="handleMouseLeave"
                  >
                    <div class="tooltip-container">{{ item.createdAt }}</div>
                  </td>
                  <td class="actions-col">
                    <div class="action-icons">
                      <div
                        :id="'view-report_btn' + i"
                        :ref="`view_report_${i}`"
                        :class="{
                          disabled: disableViewAction(item),
                        }"
                        class="ftop-icons fltr-btn tt"
                        @click="!disableViewAction(item) && viewReport(item)"
                        @mouseover="handleMouseOver($event, 'View Report', `view_report_${i}`)"
                        @mouseleave="handleMouseLeave"
                      >
                        <font-awesome-icon class="action-icon" :icon="['fas', 'eye']" />
                      </div>
                      <div
                        v-if="downloading[item.id]"
                        :id="'download-report_spinner' + i"
                        :ref="`downloading_report_${i}`"
                        class="ftop-icons fltr-btn tt"
                        @mouseover="
                          handleMouseOver(
                            $event,
                            'Downloading Report...',
                            `downloading_report_${i}`
                          )
                        "
                        @mouseleave="handleMouseLeave"
                      >
                        <font-awesome-icon
                          class="action-icon"
                          :icon="['fas', 'circle-notch']"
                          spin
                        />
                      </div>
                      <div
                        v-else
                        :id="'download-report_btn' + i"
                        :ref="`download_report_${i}`"
                        :class="{ disabled: disableDownloadReport(item) }"
                        class="ftop-icons fltr-btn tt"
                        @click="!disableDownloadReport(item) && downloadReport(item)"
                        @mouseover="
                          handleMouseOver($event, 'Download Report', `download_report_${i}`)
                        "
                        @mouseleave="handleMouseLeave"
                      >
                        <font-awesome-icon class="action-icon" :icon="['fas', 'arrow-to-bottom']" />
                      </div>
                      <div
                        v-if="emailing[item.id]"
                        :id="'emailing-report_spinner' + i"
                        :ref="`emailing_report_${i}`"
                        class="ftop-icons fltr-btn tt"
                        @mouseover="
                          handleMouseOver($event, 'Emailing Report...', `emailing_report_${i}`)
                        "
                        @mouseleave="handleMouseLeave"
                      >
                        <font-awesome-icon
                          class="action-icon"
                          :icon="['fas', 'circle-notch']"
                          spin
                        />
                      </div>
                      <div
                        v-else
                        :id="'emailing-report_btn' + i"
                        :ref="`email_report_${i}`"
                        class="ftop-icons fltr-btn tt"
                        @click="emailReport(item)"
                        @mouseover="handleMouseOver($event, 'Email Report', `email_report_${i}`)"
                        @mouseleave="handleMouseLeave"
                      >
                        <font-awesome-icon class="action-icon" :icon="['fas', 'envelope']" />
                      </div>
                      <div
                        :id="'delete-report_btn' + i"
                        :ref="`delete_report_${i}`"
                        class="ftop-icons fltr-btn tt"
                        :class="{ disabled: String(item.createdBy) !== String(currentUser.id) }"
                        @click="
                          String(item.createdBy) === String(currentUser.id)
                            ? showDeleteConfirmationPopup(item)
                            : null
                        "
                        @mouseover="
                          handleMouseOver(
                            $event,
                            String(item.createdBy) === String(currentUser.id)
                              ? 'Delete Report'
                              : 'Cannot delete the shared report',
                            `delete_report_${i}`
                          )
                        "
                        @mouseleave="handleMouseLeave"
                      >
                        <font-awesome-icon class="action-icon" :icon="['fas', 'trash']" />
                      </div>
                      <!-- Info icon for owned/shared tooltip -->
                      <div
                        :id="'ownership-info_btn' + i"
                        :ref="`ownership_info_${i}`"
                        class="ftop-icons fltr-btn tt"
                        @mouseover="
                          handleMouseOver(
                            $event,
                            getOwnedSharedTooltip(item),
                            `ownership_info_${i}`
                          )
                        "
                        @mouseleave="handleMouseLeave"
                      >
                        <font-awesome-icon class="action-icon" :icon="['fas', 'info-circle']" />
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <div v-else class="txt-center" style="margin-top: 13px;">
                No data is available to show
              </div>
            </table>
          </template>
        </div>
      </div>
      <div :style="activeEl.style" class="tooltipText">{{ activeEl.text }}</div>

      <div
        id="myScheduledReportsContainer"
        ref="table-scroll-scheduled-report"
        class="table-scroll my-scheduled-reports-container"
      >
        <div>
          <template v-if="!loading">
            <table class="tbl tbl-export" :class="css">
              <thead class="header-top">
                <tr>
                  <th
                    v-if="rowsCollapsible"
                    class="short-column sticky-column-header subRowCollapsible"
                  ></th>

                  <th
                    :ref="`${scheduledReportHeaders[0].name}_0`"
                    :class="[
                      'headers',
                      { 'sticky-column sticky-column-header': scheduledReportHeaders[0].isSticky },
                    ]"
                    @mouseover="
                      (el) =>
                        handleMouseOver(
                          el,
                          scheduledReportHeaders[0].name,
                          `${scheduledReportHeaders[0].name}_0`
                        )
                    "
                    @mouseleave="handleMouseLeave"
                  >
                    <div class="header-container">
                      <div class="header-row">
                        <span class="header-text">{{ scheduledReportHeaders[0].name }}</span>
                        <div @click="toggleSchedFilter">
                          <font-awesome-icon
                            class="filter-icon"
                            :icon="['fas', 'magnifying-glass']"
                            :class="{ rotated: showSchedFilter }"
                          />
                        </div>
                      </div>
                      <input
                        v-if="showSchedFilter"
                        ref="schedReportsUIFilter"
                        v-model="filterSchedText"
                        class="filter-input"
                        placeholder="Filter..."
                      />
                    </div>
                  </th>

                  <th
                    v-for="(header, i) in scheduledReportHeadersWithoutFirst"
                    :id="
                      'scheduled-reports-header-' + i + 1 + '-' + header.name.replace(/\s+/g, '')
                    "
                    :key="i"
                    :ref="`${header.name}_${i + 1}_sched`"
                    :class="['headers', { 'sticky-column sticky-column-header': header.isSticky }]"
                    @mouseover="
                      (el) =>
                        handleMouseOverScheduled(el, header.name, `${header.name}_${i + 1}_sched`)
                    "
                    @mouseleave="handleMouseLeaveScheduled"
                  >
                    <span>{{ header.name }}</span>
                  </th>
                </tr>
              </thead>
              <tbody v-if="filteredScheduledReports.length" class="body-half-screen">
                <tr
                  v-for="(item, i) in filteredScheduledReports"
                  :id="i"
                  :key="i"
                  :class="[{ 'sub-row': !!item.subRow }, { highlightBackgroundParent: item.open }]"
                >
                  <td
                    :ref="`row_name_${i}_sched`"
                    class="saved-reports-cell"
                    @mouseover="handleMouseOverScheduled($event, item.name, `row_name_${i}_sched`)"
                    @mouseleave="handleMouseLeaveScheduled"
                  >
                    <div class="tooltip-container">{{ item.name }}</div>
                  </td>
                  <td
                    :ref="`row_schedule_${i}_sched`"
                    @mouseover="
                      handleMouseOverScheduled($event, item.schedule, `row_schedule_${i}_sched`)
                    "
                    @mouseleave="handleMouseLeaveScheduled"
                  >
                    <div class="tooltip-container">{{ item.schedule }}</div>
                  </td>
                  <td
                    v-if="toggleLoading[item.id]"
                    :id="'active-status-spinner' + i"
                    :ref="`row_scheduleStatus_${i}_sched_loading`"
                    @mouseover="
                      handleMouseOverScheduled(
                        $event,
                        'Changing status...',
                        `row_scheduleStatus_${i}_sched_loading`
                      )
                    "
                    @mouseleave="handleMouseLeaveScheduled"
                  >
                    <font-awesome-icon
                      class="action-icon"
                      style="font-size: 25px;margin-left: 8px;"
                      :icon="['fas', 'circle-notch']"
                      spin
                    />
                  </td>
                  <td
                    v-else
                    :id="'active-status_toggle' + i"
                    :ref="`row_scheduleStatus_${i}_sched`"
                    @mouseover="
                      handleMouseOverScheduled(
                        $event,
                        item.scheduleStatus,
                        `row_scheduleStatus_${i}_sched`
                      )
                    "
                    @mouseleave="handleMouseLeaveScheduled"
                  >
                    <ToggleSwitch
                      :is-active="toggleSwitchStatus(item.scheduleStatus)"
                      @update:isActive="handleToggle(item)"
                    />
                  </td>
                  <td
                    :ref="`row_status_${i}_sched`"
                    class="saved-reports-cell"
                    @click="showMoreStatus(item)"
                    @mouseover="
                      handleMouseOverScheduled(
                        $event,
                        getStatusTooltip(item),
                        `row_status_${i}_sched`
                      )
                    "
                    @mouseleave="handleMouseLeaveScheduled"
                  >
                    <font-awesome-icon
                      class="action-icon"
                      style="font-size: 24px;margin-left: 8px;"
                      :style="{
                        color: getStatusColor(item),
                      }"
                      :icon="getStatusIcon(item)"
                    />
                  </td>
                  <td class="actions-col">
                    <div class="action-icons">
                      <div
                        v-if="savingEmail[item.id]"
                        :id="'saving-email_spinner' + i"
                        :ref="`saving_email_${i}`"
                        class="ftop-icons fltr-btn tt"
                        @mouseover="
                          handleMouseOverScheduled($event, 'Saving Emails...', `saving_email_${i}`)
                        "
                        @mouseleave="handleMouseLeaveScheduled"
                      >
                        <font-awesome-icon
                          class="action-icon"
                          :icon="['fas', 'circle-notch']"
                          spin
                        />
                      </div>
                      <!-- here you need to add that edit option -->
                      <div
                        v-else
                        :id="'edit-email_btn' + i"
                        :ref="`edit_mail_list_${i}_sched`"
                        class="ftop-icons fltr-btn tt"
                        @click="showUpdateScheduleModal(item)"
                        @mouseover="
                          handleMouseOverScheduled(
                            $event,
                            'Update Schedule & Recipients',
                            `edit_mail_list_${i}_sched`
                          )
                        "
                        @mouseleave="handleMouseLeaveScheduled"
                      >
                        <font-awesome-icon class="action-icon" :icon="['fas', 'pencil-alt']" />
                      </div>

                      <div
                        :id="'delete-sched-report_btn' + i"
                        :ref="`delete_${i}_sched`"
                        class="ftop-icons fltr-btn tt"
                        :class="{ disabled: String(item.createdBy) !== String(currentUser.id) }"
                        @click="
                          String(item.createdBy) === String(currentUser.id)
                            ? showDeleteScheduledReportPopup(item)
                            : null
                        "
                        @mouseover="
                          handleMouseOverScheduled(
                            $event,
                            String(item.createdBy) === String(currentUser.id)
                              ? 'Delete Scheduled Report'
                              : 'Cannot delete the shared report',
                            `delete_${i}_sched`
                          )
                        "
                        @mouseleave="handleMouseLeaveScheduled"
                      >
                        <font-awesome-icon class="action-icon" :icon="['fas', 'trash']" />
                      </div>
                      <div
                        :id="'ownership-info_btn_sched' + i"
                        :ref="`ownership_info_${i}_sched`"
                        class="ftop-icons fltr-btn tt"
                        @mouseover="
                          handleMouseOverScheduled(
                            $event,
                            getOwnedSharedTooltip(item),
                            `ownership_info_${i}_sched`
                          )
                        "
                        @mouseleave="handleMouseLeaveScheduled"
                      >
                        <font-awesome-icon class="action-icon" :icon="['fas', 'info-circle']" />
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <div v-else class="txt-center" style="margin-top: 13px;">
                No data is available to show
              </div>
            </table>
          </template>
        </div>
      </div>
      <div :style="activeElScheduled.style" class="tooltipTextSched">
        {{ activeElScheduled.text }}
      </div>
    </div>

    <b-loading-spinner v-if="loading" class="txt-center-full" />

    <BMultiEmailModal
      ref="savedReportsEmailComponent"
      :emails="
        toEmail?.emailRecipients && toEmail.emailRecipients.length
          ? toEmail.emailRecipients
          : [currentUser.email]
      "
      :title="`Edit Email Recipients for '${toEmail?.name || ''}'`"
      @save-email-recipients="sendEmailReport"
    />

    <div v-if="showDeleteTemplatePopup.visible" class="show-warning-popup-modal">
      <WarningConfirmModal
        :header-text="showDeleteTemplatePopup.headerText || 'Delete Report'"
        :description="showDeleteTemplatePopup.description"
        :confirm-label="showDeleteTemplatePopup.confirmLabel || 'YES'"
        :cancel-label="showDeleteTemplatePopup.cancelLabel || 'NO'"
        :loading="isDeleteLoading"
        @cancel="onCloseDeletePopup"
        @confirm="onDeleteTemplatePopup"
      />
    </div>

    <div v-if="showEditEmailWarningPopup.visible" class="show-warning-popup-modal">
      <WarningConfirmModal
        :header-text="showEditEmailWarningPopup.headerText || 'Edit Email Recipients'"
        :description="showEditEmailWarningPopup.description"
        :confirm-label="showEditEmailWarningPopup.confirmLabel || 'YES'"
        :cancel-label="showEditEmailWarningPopup.cancelLabel || 'NO'"
        @cancel="onCloseEditEmailWarningPopup"
        @confirm="onConfirmEditEmailWarningPopup"
      />
    </div>

    <div v-if="showUpdateScheduleWarningPopup.visible" class="show-warning-popup-modal">
      <WarningConfirmModal
        :header-text="showUpdateScheduleWarningPopup.headerText || 'Update Schedule & Recipients'"
        :description="showUpdateScheduleWarningPopup.description"
        :confirm-label="showUpdateScheduleWarningPopup.confirmLabel || 'YES'"
        :cancel-label="showUpdateScheduleWarningPopup.cancelLabel || 'NO'"
        @cancel="onCloseUpdateScheduleWarningPopup"
        @confirm="onConfirmUpdateScheduleWarningPopup"
      />
    </div>

    <b-update-scheduled-report
      ref="emailRecipientComponent"
      :emails="emailTo"
      :schedule-item="scheduleItem"
      @save-report-schedule-info="udpateReportScheduleInfo"
    />
    <b-report-run-status ref="runStatusComponent" />
  </div>
</template>

<script>
import { get } from 'vuex-pathify';
import EventBus from '@/adready-vue/helpers/global/event-bus';
import fileDownload from 'js-file-download';
import moment from 'moment';
import reportBuilderV2API from '@/api/report-builder-v2-api';
import advertiserReportsApi from '@/api/advertiser-reports';
import { buildQueryString } from '@/helpers/global/url-helpers';
import {
  ACCOUNTS_TO_SHOW,
  ALL_TOUCH_REPORT,
  DEFAULT_METHODOLOGY,
  INSIGHTS_PRESET_TEMPLATE_NAMES,
  LAST_TOUCH_REPORT,
  MAX_RUN_REPORT_COUNT_REACH_ERROR_MSG,
  MESSAGES,
  PRE_DEFINED_TEMPLATES,
  PRE_DEFINED_TEMPLATES_WITH_NO_AUDIENCE_INSIGHTS,
  REPORT_BUILDER_2_EVENT_MAP,
  RUN_REPORT_TIMESTAMPS_KEY,
  FIRST_DAG_SUBMISSION_FAILURE_MESSAGE,
  PARTIAL_SUCCESS,
} from '@/constant';
import ToggleSwitch from '@/components/elements/b-toggle-switch.vue';
import { isBlank } from 'adready-api/helpers/common';
import WarningConfirmModal from './warning-confirm-modal.vue';
import BUpdateScheduledReport from './b-update-scheduled-report.vue';
import BMultiEmailModal from './b-multi-email-modal.vue';
import BReportRunStatus from './b-report-run-status.vue';
import * as util from '~/util/utility-functions';

const headers = [
  { name: 'Saved Reports' },
  { name: 'Date Range' },
  { name: 'Created' },
  { name: 'Actions' },
];

const scheduledReportHeaders = [
  { name: 'Scheduled Reports' },
  { name: 'Scheduled' },
  { name: 'Active' },
  { name: 'Status' },
  { name: 'Actions' },
];

export default {
  components: {
    BLoadingSpinner: () =>
      import(
        /* webpackChunkName: "b-loading-spinner" */ '~/components/elements/b-loading-spinner.vue'
      ),
    BUpdateScheduledReport,
    BReportRunStatus,
    WarningConfirmModal,
    ToggleSwitch,
    BMultiEmailModal,
  },
  props: {
    rows: { type: Array, default: () => [] },
    scheduledReports: { type: Array, default: () => [] },
    headers: { type: Array, default: () => headers },
    scheduledReportHeaders: { type: Array, default: () => scheduledReportHeaders },
    rowToScrollTo: { type: String, default: '' },
    rowsCollapsible: { type: Boolean, default: false },
    css: { type: String, default: '' },
    isPageView: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },
  data() {
    return {
      activeEl: { style: { display: 'none' }, text: '' },
      activeElScheduled: { style: { display: 'none' }, text: '' },
      showMessage: false,
      message: '',
      messageType: 'success',
      showDeleteTemplatePopup: { visible: false, payload: {} },
      showEditEmailWarningPopup: { visible: false, payload: {} },
      showUpdateScheduleWarningPopup: { visible: false, payload: {} },
      isDeleteLoading: false,
      downloading: {},
      emailing: {},
      emailSentStatus: '',
      openEmailPopup: false,
      toEmail: {},
      toggleLoading: {},
      emailTo: [],
      scheduleItem: {},
      savingEmail: {},

      showFilter: false,
      filterText: '',
      showSchedFilter: false,
      filterSchedText: '',
      allSubCategoriesSelected: false,
    };
  },
  computed: {
    incReachMetricsData: get('dashboard/incReachMetricsData'),
    advertiser: get('common/advertiser'),
    account: get('common/account'),
    universalPixelId: get('common/universalPixelId'),
    attribution: get('dashboard/filters@attribution'),
    eventOptions: get('dashboard/filters@eventOptions'),
    selectedEvent: get('dashboard/GET_SELECTED_EVENT'),
    currentUser: get('common/currentUser'),
    selectedEventLabel() {
      return isBlank(this.selectedEvent.eventLabel) ? 'Event' : this.selectedEvent.eventLabel;
    },
    headersWithoutFirst() {
      return [...this.headers].splice(1);
    },
    filteredRows() {
      if (!this.filterText) {
        return this.rows;
      }
      return this.rows.filter((row) =>
        row.name.toLowerCase().includes(this.filterText.toLowerCase())
      );
    },
    scheduledReportHeadersWithoutFirst() {
      return [...this.scheduledReportHeaders].splice(1);
    },
    filteredScheduledReports() {
      if (!this.filterSchedText) {
        return this.scheduledReports;
      }
      return this.scheduledReports.filter((row) =>
        row.name.toLowerCase().includes(this.filterSchedText.toLowerCase())
      );
    },
  },
  watch: {
    error(newValue) {
      if (newValue) {
        this.showErrorMessage(newValue);
      }
    },
  },
  beforeDestroy() {
    this.toEmail = {};
  },
  mounted() {
    this.scrollToRow(this.rowToScrollTo);
    this.toEmail = {};
  },
  methods: {
    isDemoTouchItem(item) {
      return (
        util.isDemoInstance() &&
        [ALL_TOUCH_REPORT, LAST_TOUCH_REPORT].includes(
          item?.filters?.template || item?.filters?.dimensions?.[0]
        )
      );
    },
    disableViewAction(item) {
      if (this.isDemoTouchItem(item)) return false;
      let disableView = false;
      if (
        (item?.filters?.dimensions.length === 1 &&
          PRE_DEFINED_TEMPLATES.includes(item?.filters?.dimensions[0])) ||
        item.filters.presetTemplateName
      ) {
        disableView = true;
      }
      return disableView;
    },
    disableDownloadReport(item) {
      if (this.isDemoTouchItem(item)) return false;
      let disableDownload = false;
      if (
        (item?.filters?.dimensions.length === 1 &&
          PRE_DEFINED_TEMPLATES.includes(item?.filters?.dimensions[0])) ||
        item.filters.presetTemplateName
      ) {
        disableDownload = true;
      }
      return disableDownload;
    },
    toggleFilter() {
      this.showFilter = !this.showFilter;
      if (this.showFilter) {
        this.$nextTick(() => {
          this.$refs.savedReportsUIFilter.focus();
        });
      }
      if (!this.showFilter) {
        this.filterText = ''; // Clear filter when closing
      }
    },
    toggleSchedFilter() {
      this.showSchedFilter = !this.showSchedFilter;
      if (this.showSchedFilter) {
        this.$nextTick(() => {
          this.$refs.schedReportsUIFilter.focus();
        });
      }
      if (!this.showSchedFilter) {
        this.filterSchedText = ''; // Clear filter when closing
      }
    },
    getStatusTooltip(item) {
      if (item.lastRunDate && item.lastRunStatus) {
        if (item.lastRunStatus.toLowerCase() === 'success') {
          return `${'Last Run Status: Successful\nLast Run Date: '}${
            item.lastRunDate
          }\n\nClick to show Run History`;
        }
        return `${'Last Run Status: Failed\nLast Run Date: '}${
          item.lastRunDate
        }\n\nClick to show Run History`;
      }
      return 'Pending';
    },
    getStatusColor(item) {
      if (item.lastRunDate && item.lastRunStatus) {
        if (item.lastRunStatus.toLowerCase() === 'success') {
          return '#00ff00'; // Green
        }
        return '#ff0000'; // Red
      }
      return '#aaaaaa'; // Default gray
    },
    getStatusIcon(item) {
      if (item.lastRunDate && item.lastRunStatus) {
        if (item.lastRunStatus.toLowerCase() === 'success') {
          return ['far', 'check-circle'];
        }
        return ['far', 'times-circle'];
      }
      return ['far', 'circle-dashed'];
    },
    handleMouseOver(el, text, refKey, isRow = false) {
      const elementPos = this.$refs[refKey][0];
      const tableElement = this.$refs['table-scroll'];
      const addScrollTop = isRow && !this.isPageView ? 170 : 35;

      if (!elementPos) return;

      this.activeEl = {
        style: {
          top: `${elementPos.offsetParent.offsetTop - tableElement.scrollTop + addScrollTop}px`,
          left: `${elementPos.offsetLeft - tableElement.scrollLeft + 40}px`,
          display: 'block',
        },
        text,
      };
    },
    handleMouseLeave() {
      this.activeEl = { style: { display: 'none' }, text: '' };
    },
    handleMouseOverScheduled(el, text, refKey, isRow = false) {
      const elementPos = this.$refs[refKey][0];
      const tableElement = this.$refs['table-scroll-scheduled-report'];
      const addScrollTop = isRow && !this.isPageView ? 170 : 35;

      if (!elementPos) return;

      this.activeElScheduled = {
        style: {
          top: `${elementPos.offsetParent.offsetTop - tableElement.scrollTop + addScrollTop}px`,
          left: `${elementPos.offsetLeft - tableElement.scrollLeft + 640}px`,
          display: 'block',
        },
        text,
      };
    },
    handleMouseLeaveScheduled() {
      this.activeElScheduled = { style: { display: 'none' }, text: '' };
    },
    scrollToRow(rowId) {
      const row = this.$refs['table-scroll']?.querySelector(`table tbody tr[id="${rowId}"]`);
      if (row) this.$refs['table-scroll']?.scrollTo(0, row.offsetTop - row.offsetHeight - 5);
    },
    genCSVFileName(item) {
      return `${item.name}-${moment(new Date()).format('yyyy-MM-DD')}.csv`;
    },
    saveCSV(data, fileName = 'file.csv') {
      fileDownload(data, fileName);
    },
    emailReport(item) {
      this.toEmail = item;
      this.$refs.savedReportsEmailComponent.openClosePopup();
    },
    async triggerTouchReportEmail(payload, email) {
      this.applyTemplateFilters(payload.filters);
      const payLoad = this.preparePayload(payload);
      delete payLoad.interval;
      delete payLoad.showUniques;
      delete payLoad.attribution;
      delete payLoad.methodology;
      payLoad.kind =
        payload?.filters?.template?.toLowerCase() ||
        payload?.filters?.dimensions[0]?.toLowerCase() ||
        '';
      payLoad.includeChannel = !!ACCOUNTS_TO_SHOW.includes(this.account?.id);
      if (this.allSubCategoriesSelected) {
        delete payLoad.subCategory;
      }
      try {
        const filters = {
          ...payLoad,
          emailAddr: email,
          onceNow: true,
          emailIt: true,
        };
        const opts = {
          headers: {
            Accept: 'application/json',
          },
          respondHeaders: true,
        };
        const response = await advertiserReportsApi.reportsRollup(
          this.advertiser.id,
          buildQueryString(filters),
          opts
        );
        this.emailSentStatus = 'success';
        this.$set(this.emailing, this.toEmail.id, false);
        if (response?.data?.message === PARTIAL_SUCCESS) {
          this.showSuccessMessage(FIRST_DAG_SUBMISSION_FAILURE_MESSAGE);
        } else {
          this.showSuccessMessage(`Report has been sent to the email id: ${email} successfully.`);
        }
      } catch (err) {
        const errorMessage = util.getErrorMessage(err, false);
        console.error('error sending email ->', err);
        this.emailSentStatus = 'error';
        this.showErrorMessage(errorMessage);
        this.$set(this.emailing, this.toEmail.id, false);
        throw err;
      } finally {
        setTimeout(() => {
          this.emailSentStatus = '';
          this.$set(this.emailing, this.toEmail.id, false);
          this.openEmailPopup = false;
        }, 1000);
      }
    },
    showMoreStatus(item) {
      this.$refs.runStatusComponent.openClosePopup(item);
      // get updated status
      reportBuilderV2API
        .getScheduledReportStatus([item.advertiserId, item.id])
        .then((response) => {
          if (response) {
            this.$set(item, 'lastRunDate', response.lastRunDate);
            this.$set(item, 'lastRunStatus', response.lastRunStatus);
            item.lastRunDate = response.lastRunDate;
            item.lastRunStatus = response.lastRunStatus;
            this.$forceUpdate();
          }
        })
        .catch((error) => {
          console.error(error);
        });
      // setting to true to fetch updated report list
      EventBus.fetchSavedReports = true;
    },
    async triggerEpEmail(payload, email) {
      const payLoad = this.preparePayload(payload);
      const selectedReportKind =
        payload?.filters?.template?.toLowerCase() ||
        payload?.filters?.dimensions[0]?.toLowerCase() ||
        '';

      try {
        const filters = {
          ...payLoad,
          event: 'Leads',
          category: 'lead',
          kind: selectedReportKind,
          emailAddr: email,
          onceNow: true,
          emailIt: true,
        };
        const opts = {
          headers: {
            Accept: 'application/json',
          },
          respondHeaders: true,
        };
        await advertiserReportsApi.reportsEventPerformance(
          this.advertiser.id,
          buildQueryString(filters),
          opts
        );

        this.emailSentStatus = 'success';
        this.$set(this.emailing, this.toEmail.id, false);
        this.showSuccessMessage(MESSAGES.REPORT_EMAIL_SUCCESS(email));
      } catch (err) {
        const errorMessage = util.getErrorMessage(err, false);
        console.error('error sending email ->', err);
        this.emailSentStatus = 'error';
        this.showErrorMessage(errorMessage);
        this.$set(this.emailing, this.toEmail.id, false);
        throw err;
      } finally {
        setTimeout(() => {
          this.emailSentStatus = '';
          this.$set(this.emailing, this.toEmail.id, false);
          this.openEmailPopup = false;
        }, 1000);
      }
    },
    sendEmailReport(email = '') {
      // If email is an array, join as comma separated string backend will handle it automactically
      const emailStr = Array.isArray(email) ? email.join(',') : email;

      setTimeout(() => {
        this.openEmailPopup = false;
        // this.$refs.savedReportsEmailComponent.openClosePopup();
      }, 500);

      const isMaxRunCountReach = util.verifyRunCount(RUN_REPORT_TIMESTAMPS_KEY);
      if (isMaxRunCountReach) {
        this.showErrorMessage(MAX_RUN_REPORT_COUNT_REACH_ERROR_MSG);

        this.emailSentStatus = ' ';
        this.$set(this.emailing, this.toEmail.id, false);
        setTimeout(() => {
          this.emailSentStatus = '';
        }, 500);
        return;
      }

      this.$set(this.emailing, this.toEmail.id, true);
      if (this.toEmail.filters.reportMetrics.some((metric) => metric?.metrics === 'ep')) {
        this.triggerEpEmail(this.toEmail, emailStr);
        return;
      }
      if (
        this.toEmail.filters.dimensions.length === 1 &&
        PRE_DEFINED_TEMPLATES_WITH_NO_AUDIENCE_INSIGHTS.includes(this.toEmail.filters.dimensions[0])
      ) {
        this.triggerTouchReportEmail(this.toEmail, emailStr);
        return;
      }
      const payload = {
        id: this.toEmail.id,
        advertiserId: this.toEmail.advertiserId,
        emailTo: emailStr,
        email: true,
      };

      let isInsights = false;
      if (INSIGHTS_PRESET_TEMPLATE_NAMES.includes(this.toEmail.filters.presetTemplateName)) {
        isInsights = true;
      }

      const api = !isInsights ? reportBuilderV2API.runReport : reportBuilderV2API.runPresetReport;
      delete payload.offset;
      delete payload.limit;

      const opts = {
        headers: {
          'Content-Type': 'application/json',
        },
        respondHeaders: true,
      };
      api(payload.advertiserId, payload, opts)
        .then((response) => {
          if (response?.status === 200) {
            this.emailSentStatus = 'success';
            if (response?.result?.firstAttemptFailed) {
              this.showSuccessMessage(FIRST_DAG_SUBMISSION_FAILURE_MESSAGE);
            } else {
              this.showSuccessMessage(MESSAGES.REPORT_EMAIL_SUCCESS(emailStr));
            }
            this.$set(this.emailing, this.toEmail.id, false);
          }
        })
        .catch((error) => {
          const errorMessage = util.getErrorMessage(error, false);
          console.error('Error:', errorMessage);
          this.showErrorMessage(errorMessage);
          this.emailSentStatus = 'error';
          this.$set(this.emailing, this.toEmail.id, false);
        })
        .finally(() => {
          setTimeout(() => {
            this.emailSentStatus = '';
            this.$set(this.emailing, this.toEmail.id, false);
            this.openEmailPopup = false;
          }, 1000);
        });
    },
    preparePayload(payload) {
      const selectedEventFromPayload = this.getSelectedEventFromPayload(payload);
      const eventSubCategory = payload?.filters?.reportMetrics?.[0]?.subCategory;
      this.allSubCategoriesSelected =
        selectedEventFromPayload.numSubCategories === eventSubCategory?.length;
      return {
        advertiser: this.advertiser?.name || '',
        client: this.account?.name || '',
        pixel: this.universalPixelId,
        mediaType: payload?.filters?.mediaTypes,
        ioIds: payload?.filters?.campaignIds,
        startDate: payload?.filters?.startDate,
        endDate: payload?.filters?.endDate,
        adGroups: payload?.filters?.adGroups ?? '',
        audience: payload?.filters?.audiences,
        publisher: payload?.filters?.publisher,
        creative: payload?.filters?.creatives,
        conversionWindow:
          payload?.filters?.conversionWindowUnit === 'DAYS'
            ? payload?.filters?.conversionWindow * 24
            : payload?.filters?.conversionWindow || 744,
        showUniques: payload?.filters?.showUniques,
        methodology: payload?.filters?.methodology?.toLowerCase() || DEFAULT_METHODOLOGY,
        attribution: this.attribution,
        event: selectedEventFromPayload.event,
        category: isBlank(selectedEventFromPayload.category)
          ? 'none'
          : selectedEventFromPayload.category,
        subCategory: eventSubCategory,
        interval: payload?.filters?.interval || 'CUMULATIVE',
        reportBuilderV2: true,
        templateName: payload?.name ?? '',
        dateRangeType: payload?.filters?.dateRangeType ?? '',
      };
    },
    getSelectedEventFromPayload(payload) {
      const metricKey = payload?.filters?.reportMetrics?.[0]?.metrics;
      if (!metricKey) {
        return '';
      }
      const eventName = REPORT_BUILDER_2_EVENT_MAP[metricKey] || metricKey;
      return this.eventOptions.find((item) => item.event === eventName) || '';
    },
    async triggerTouchReportsDownload(payload) {
      this.applyTemplateFilters(payload.filters);
      const payLoad = this.preparePayload(payload);
      delete payLoad.interval;
      delete payLoad.showUniques;
      delete payLoad.attribution;
      delete payLoad.methodology;
      payLoad.kind =
        payload?.filters?.template?.toLowerCase() ||
        payload?.filters?.dimensions[0]?.toLowerCase() ||
        '';
      payLoad.includeChannel = !!ACCOUNTS_TO_SHOW.includes(this.account?.id);
      if (this.selectedEvent.allSubCategoriesSelected) {
        delete payLoad.subCategory;
      }
      const opts = {
        headers: {
          Accept: 'application/json',
        },
        respondHeaders: true,
      };
      opts.headers.Accept = 'text/csv';
      opts.responseType = 'blob';
      try {
        const res = await advertiserReportsApi.reportsRollup(
          this.advertiser.id,
          buildQueryString(payLoad),
          opts
        );
        this.saveCSV(res.data, this.genCSVFileName(payload));
        this.$set(this.downloading, payload.id, false);
      } catch (err) {
        const errorMessage = util.getErrorMessage(err, true);
        this.showErrorMessage(errorMessage);
        console.error('error downloading file -> ', err);
        this.$set(this.downloading, payload.id, false);
        throw err;
      } finally {
        this.$set(this.downloading, payload.id, false);
      }
    },
    async triggerEpDownload(payload) {
      let payLoad = this.preparePayload(payload);
      const selectedReportKind =
        payload?.filters?.template?.toLowerCase() ||
        payload?.filters?.dimensions[0]?.toLowerCase() ||
        '';

      payLoad = {
        ...payLoad,
        kind: selectedReportKind,
        fields: ['name', 'ep'],
        event: 'Leads',
        category: 'lead',
      };
      try {
        const opts = {
          headers: {
            Accept: 'text/csv',
          },
          respondHeaders: true,
          responseType: 'blob',
        };
        const res = await advertiserReportsApi.reportsEventPerformance(
          this.advertiser.id,
          buildQueryString(payLoad),
          opts
        );
        this.saveCSV(res.data, this.genCSVFileName(payload));
        this.$set(this.downloading, payload.id, false);
      } catch (err) {
        const errorMessage = util.getErrorMessage(err, true);
        this.showErrorMessage(errorMessage);
        console.error('error downloading file -> ', err);
        this.$set(this.downloading, payload.id, false);
        throw err;
      } finally {
        this.$set(this.downloading, payload.id, false);
      }
    },
    downloadReport(item) {
      const isMaxRunCountReach = util.verifyRunCount(RUN_REPORT_TIMESTAMPS_KEY);
      if (isMaxRunCountReach) {
        this.showErrorMessage(MAX_RUN_REPORT_COUNT_REACH_ERROR_MSG);
        return;
      }
      this.$set(this.downloading, item.id, true);
      if (item.filters.reportMetrics.some((metric) => metric?.metrics === 'ep')) {
        this.triggerEpDownload(item);
        return;
      }
      if (
        item.filters.dimensions.length === 1 &&
        PRE_DEFINED_TEMPLATES.includes(item.filters.dimensions[0])
      ) {
        this.triggerTouchReportsDownload(item);
        return;
      }
      const payload = {
        id: item.id,
        export: true,
      };
      const opts = {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/csv',
        },
        responseType: 'blob',
      };
      reportBuilderV2API
        .runReport(item.advertiserId, payload, opts)
        .then((response) => {
          if (response?.status === 200) {
            this.saveCSV(response.data, this.genCSVFileName(item));
          }
        })
        .catch((error) => {
          const errorMessage = util.getErrorMessage(error, true);
          console.error('Error:', errorMessage);
          this.$set(this.downloading, item.id, false);
          this.showErrorMessage(errorMessage);
        })
        .finally(() => {
          this.$set(this.downloading, item.id, false);
        });
    },
    triggerEpSearch(payload) {
      this.$nextTick(() => {
        if (EventBus.listenerReady) {
          EventBus.$emit('do-new-report-search', payload);
        } else {
          console.log('Listener not ready, will retry...');
          setTimeout(() => this.triggerEpSearch(payload), 100);
        }
      });
    },
    triggerTouchReportsSearch(payload) {
      this.$nextTick(() => {
        if (EventBus.listenerReady) {
          // If the listener is ready, emit the event
          EventBus.$emit('do-new-report-search', payload);
        } else {
          // If the listener isn't ready, retry after a short delay
          setTimeout(this.triggerSearch(payload), 100); // Retry after 100ms
        }
      });
    },
    viewReport(item) {
      const isMaxRunCountReach = util.verifyRunCount(RUN_REPORT_TIMESTAMPS_KEY);
      if (isMaxRunCountReach) {
        this.showErrorMessage(MAX_RUN_REPORT_COUNT_REACH_ERROR_MSG);
        return;
      }
      if (item.filters.reportMetrics.some((metric) => metric?.metrics === 'ep')) {
        EventBus.$emit('switch-to-tab-index', 2, item.name);
        this.triggerEpSearch(item);
        return;
      }
      if (
        item.filters.dimensions.length === 1 &&
        PRE_DEFINED_TEMPLATES.includes(item.filters.dimensions[0])
      ) {
        EventBus.$emit('switch-to-tab-index', 2, item.name);
        this.applyTemplateFilters(item.filters);
        this.triggerTouchReportsSearch(item);
        return;
      }
      const payload = {
        id: item.id,
        advertiserId: item.advertiserId,
        name: item.name,
        filters: item.filters,
      };
      EventBus.$emit('switch-to-tab-index', 2, item.name);
      this.triggerSearch(payload);
    },
    applyTemplateFilters(templateFilters) {
      this.eventOptions.forEach((event) => {
        const matchedEvent = templateFilters.reportMetrics.find((item) => {
          const formattedMetric = this.formatEventNamesReverse(item.metrics);
          if (
            (item.metrics === 'completions' || item.metrics === 'clicks') &&
            formattedMetric === event.event
          ) {
            // Allow item.event to be false for these specific cases
            return true;
          }
          // Default logic for other cases
          return formattedMetric === event.event && item.event === true;
        });
        if (matchedEvent) {
          event.selected = true;
          if (event.children && event.children.length > 0) {
            event.children.forEach((subCategory) => {
              const matchedSubCategory = matchedEvent.subCategory.find(
                (sub) => sub === subCategory.category
              );
              subCategory.selected = !!matchedSubCategory;
            });
          }
        } else {
          event.selected = false;
          if (event.children && event.children.length > 0) {
            event.children.forEach((subCategory) => {
              subCategory.selected = false;
            });
          }
        }
      });
    },
    formatEventNamesReverse(name) {
      const eventMap = {
        lead: 'Leads',
        checkout: 'Checkouts',
        revenue: 'Revenue',
        pageVisit: 'Page Visits',
        completions: 'Video Completions',
        clicks: 'Clicks',
        locationVisit: 'Location Visits',
        activity: 'Activities',
      };
      return eventMap[name] || name;
    },
    triggerSearch(payload) {
      if (EventBus.myReportsListenerReady) {
        EventBus.$emit('do-new-report-search-from-my-reports', payload);
      } else {
        setTimeout(() => this.triggerSearch(payload), 200);
      }
    },
    async deleteReport(item) {
      await reportBuilderV2API
        .deleteReport([item.advertiserId, item.id])
        .then((response) => {
          if (response?.status === 200) {
            // setting to true to fetch updated report list
            EventBus.fetchSavedReports = true;

            this.showSuccessMessage(`${item.name} has been deleted.`);

            const index = this.rows.indexOf(item);
            if (index > -1) {
              this.rows.splice(index, 1);
            }

            const indexSch = this.scheduledReports.indexOf(item);
            if (indexSch > -1) {
              this.scheduledReports.splice(indexSch, 1);
            }
          }
        })
        .catch((error) => {
          console.error(`error processing report ->`, error);
          this.showErrorMessage(
            `Encountered an error deleting ${item.name}. Please contact administrator.`
          );
        })
        .finally(() => {
          this.$nextTick(() => {
            this.isDeleteLoading = false;
            this.onCloseDeletePopup();
          });
        });
    },
    showSuccessMessage(msg) {
      this.message = msg;
      this.messageType = 'success';
      this.displayMessage();
    },
    showErrorMessage(msg) {
      this.message = msg;
      this.messageType = 'error';
      this.displayMessage();
    },
    displayMessage() {
      this.showMessage = true;
      setTimeout(() => {
        this.showMessage = false;
      }, 5000);
    },
    showDeleteConfirmationPopup(item) {
      this.showDeleteTemplatePopup = {
        visible: true,
        description: `Delete report ${item.name}?`,
        payload: {
          item,
        },
      };
    },
    showDeleteScheduledReportPopup(item) {
      this.showDeleteTemplatePopup = {
        visible: true,
        description: `Delete scheduled report ${item.name}?`,
        payload: {
          item,
        },
      };
    },
    onCloseDeletePopup() {
      this.showDeleteTemplatePopup = { visible: false, payload: {} };
    },
    onDeleteTemplatePopup() {
      this.isDeleteLoading = true;
      this.$nextTick(() => {
        const { payload } = this.showDeleteTemplatePopup;
        this.deleteReport(payload.item);
      });
    },
    toggleSwitchStatus(status) {
      return status === 'ACTIVE';
    },
    async handleToggle(item) {
      this.$set(this.toggleLoading, item.id, true);
      this.$emit('update-data-loading', true);

      const prevStatus = item.scheduleStatus;

      delete item.createdAt;
      delete item.createdBy;
      delete item.updatedAt;
      delete item.updatedBy;
      item.scheduleStatus = item.scheduleStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

      // setting to true to fetch updated report list
      EventBus.fetchSavedReports = true;
      await reportBuilderV2API
        .toggleStatus([this.advertiser?.id, item.id], item)
        .then((response) => {
          if (response?.data?.status === 200) {
            this.$set(this.toggleLoading, item.id, false);
          }
        })
        .catch((error) => {
          const errorMessage = util.getErrorMessage(error, false);
          item.scheduleStatus = prevStatus;
          this.showErrorMessage(errorMessage);
        })
        .finally(() => {
          this.$set(this.toggleLoading, item.id, false);
          this.$emit('update-data-loading', false);
          // setting to true to fetch updated report list
          EventBus.fetchSavedReports = true;
        });
    },
    showUpdateScheduleModal(item) {
      // Open the edit modal directly, do not show warning modal here
      this.emailTo = item.emailTo ? item.emailTo.split(',').map((email) => email.trim()) : [];
      this.scheduleItem = item;
      this.$refs.emailRecipientComponent.openClosePopup();
    },

    async udpateReportScheduleInfo(toEmails, frequency, scheduleStartDate, scheduleEndDate) {
      setTimeout(() => {
        this.$refs.emailRecipientComponent.openClosePopup();
      });

      this.$set(this.savingEmail, this.scheduleItem.id, true);
      this.$emit('update-data-loading', true);
      const emailArray = this.scheduleItem.emailTo.split(',').map((email) => email?.trim());
      const cleanEmailArray = Array.isArray(toEmails) ? toEmails.map((email) => email?.trim()) : [];

      const isScheduleEqual = this.scheduleItem.schedule === frequency;
      const isStartDateEqual = this.scheduleItem.scheduleStartDate === scheduleStartDate;
      const isEndDateEqual = this.scheduleItem.scheduleEndDate === scheduleEndDate;

      const removedEmails = emailArray.filter((email) => !cleanEmailArray.includes(email));
      const addedEmails = cleanEmailArray.filter((email) => !emailArray.includes(email));

      // Show modal only if emails are removed or schedule fields changed
      if (removedEmails.length > 0 || !isScheduleEqual || !isStartDateEqual || !isEndDateEqual) {
        this.showUpdateScheduleWarningPopup = {
          visible: true,
          headerText: 'Update Schedule & Recipients',
          description: `Are you sure you want to update the schedule and email recipients for ${this.scheduleItem.name}?`,
          confirmLabel: 'YES',
          cancelLabel: 'NO',
          payload: {
            item: {
              ...this.scheduleItem,
              emailTo: cleanEmailArray.join(','),
              schedule: frequency,
              scheduleStartDate,
              scheduleEndDate,
            },
          },
        };
        this.$set(this.savingEmail, this.scheduleItem.id, false);
        this.$emit('update-data-loading', false);
        return;
      }

      // If only new emails are added, update directly without modal
      if (
        addedEmails.length > 0 &&
        removedEmails.length === 0 &&
        isScheduleEqual &&
        isStartDateEqual &&
        isEndDateEqual
      ) {
        const payload = this.scheduleItem;
        delete payload.createdAt;
        delete payload.updatedAt;
        delete payload.updatedBy;
        payload.emailTo = cleanEmailArray.join(',');
        EventBus.fetchSavedReports = true;
        await reportBuilderV2API
          .updateReportTemplate([this.advertiser?.id, payload.id], payload)
          .then((response) => {
            if (response?.data?.status === 200) {
              this.showSuccessMessage(
                `Successfully updated Email Recipients for ${this.scheduleItem.name}`
              );
              this.$set(this.savingEmail, this.scheduleItem.id, false);
            }
          })
          .catch((error) => {
            const errorMessage = util.getErrorMessage(error, false);
            payload.emailTo = emailArray.join(',');
            this.showErrorMessage(errorMessage);
          })
          .finally(() => {
            this.$set(this.savingEmail, this.scheduleItem.id, false);
            this.$emit('update-data-loading', false);
            this.$emit('refresh-saved-reports');
            EventBus.fetchSavedReports = true;
          });
      }
      this.$set(this.savingEmail, this.scheduleItem.id, false);
      this.$emit('update-data-loading', false);
    },

    onConfirmUpdateScheduleWarningPopup() {
      // Actually perform the update after user confirms
      const { payload } = this.showUpdateScheduleWarningPopup;
      this.$nextTick(async () => {
        this.showUpdateScheduleWarningPopup = { visible: false, payload: {} };
        this.$set(this.savingEmail, payload.item.id, true);
        this.$emit('update-data-loading', true);
        // Remove unnecessary fields
        const updatePayload = { ...payload.item };
        delete updatePayload.createdAt;
        delete updatePayload.updatedAt;
        delete updatePayload.updatedBy;
        // setting to true to fetch updated report list
        EventBus.fetchSavedReports = true;
        await reportBuilderV2API
          .updateReportTemplate([this.advertiser?.id, updatePayload.id], updatePayload)
          .then((response) => {
            if (response?.data?.status === 200) {
              this.showSuccessMessage(
                `Successfully updated schedule and recipients for ${updatePayload.name}`
              );
              this.$set(this.savingEmail, updatePayload.id, false);
            }
          })
          .catch((error) => {
            const errorMessage = util.getErrorMessage(error, false);
            this.showErrorMessage(errorMessage);
          })
          .finally(() => {
            this.$set(this.savingEmail, updatePayload.id, false);
            this.$emit('update-data-loading', false);
            this.$emit('refresh-saved-reports');
            EventBus.fetchSavedReports = true;
          });
      });
    },
    onCloseUpdateScheduleWarningPopup() {
      this.showUpdateScheduleWarningPopup = { visible: false, payload: {} };
      // Reset edit modal fields to original scheduled report data
      if (this.scheduleItem && this.scheduleItem.id) {
        // Re-fetch the original item from scheduledReports by id
        const original = this.scheduledReports.find((r) => r.id === this.scheduleItem.id);
        if (original) {
          this.emailTo = original.emailTo ? original.emailTo.split(',').map((e) => e.trim()) : [];
          this.scheduleItem = { ...original };
        }
      }
    },
    getOwnedSharedTooltip(item) {
      if (String(item.createdBy) === String(this.currentUser.id)) {
        return 'Owned by you';
      }
      // Use createdByUserName if available, fallback to createdByUserEmail
      return item.createdByUserName
        ? `Owned by ${item.createdByUserName}`
        : `Owned by ${item.createdByUserEmail}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@media screen {
  * {
    box-sizing: border-box;
  }
  .table-wrap {
    .tbl {
      thead {
        z-index: 4;
        background-color: var(--primarycolor) !important;
      }
      tr td {
        word-wrap: break-word;
        white-space: normal;
        overflow-wrap: break-word;
      }
    }
    .table-scroll {
      width: 50%;
      max-height: 36.5rem !important;
      overflow: auto;
    }
    .message {
      white-space: pre-line;
    }
    .message-container {
      margin-bottom: 10px;
      padding: 10px;
      text-align: center;
      border-radius: 4px;
    }
    .message.success {
      background-color: #d4edda;
      color: #155724;
      border: 1px solid #c3e6cb;
    }
    .message.error {
      background-color: #f8d7da;
      color: #721c24;
      border: 1px solid #f5c6cb;
    }
  }
  table.tbl-export {
    & thead {
      padding-bottom: unset;
      border-bottom: unset;
    }
    & td.short-column.sticky-column.subRowCollapsible {
      width: 26px !important;
      overflow: unset;
    }
    & th.short-column.sticky-column-header.subRowCollapsible {
      width: 26px !important;
    }
    .sticky-column {
      z-index: 1;
      background-color: #212 429;
      &.subRowCollapsible {
        left: 0px;
      }
      &.sub-row-column-highlight {
        background-color: #191919;
      }
      &-header {
        background: var(--primarycolor);
        z-index: 5;
        &.subRowCollapsible {
          left: 0px;
        }
      }
    }
  }
  .body-half-screen {
    .relative {
      position: relative;
    }
    tr {
      position: relative;
    }
    tr td.highlightBorder {
      border-right: 2px solid var(--primarycolor);
      left: 1px;
      z-index: 3;
      height: 3em;
    }
  }
  .tooltipText {
    position: absolute;
    z-index: 999;
    width: auto;
    font-size: 12px;
    color: #222;
    pointer-events: none;
    background-color: rgba(245, 245, 250, 0.9);
    text-align: center;
    border-radius: 3px;
    transition: ease 0.3s;
    line-height: 1.2em;
    transform-origin: bottom center;
    padding: 5px;
    display: none;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
  .tooltipTextSched {
    position: absolute;
    z-index: 999;
    width: auto;
    font-size: 12px;
    color: #222;
    pointer-events: none;
    background-color: rgba(245, 245, 250, 0.9);
    text-align: left;
    border-radius: 3px;
    transition: ease 0.3s;
    line-height: 1.2em;
    transform-origin: bottom center;
    padding: 5px;
    display: none;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    white-space: pre-line;
  }
  .tooltipTextSched {
    position: absolute;
    z-index: 999;
    width: auto;
    font-size: 12px;
    color: #222;
    pointer-events: none;
    background-color: rgba(245, 245, 250, 0.9);
    text-align: left;
    border-radius: 3px;
    transition: ease 0.3s;
    line-height: 1.2em;
    transform-origin: bottom center;
    padding: 5px;
    display: none;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    white-space: pre-line;
  }
  .highlightBackgroundParent {
    background-color: #191919;
    border-bottom: none;
    > .sticky-column {
      background-color: #191919;
      &:nth-child(n + 2):nth-child(-n + 5) {
        background-color: #191919;
        &:hover {
          background-color: #373a44;
        }
      }
      &:hover {
        background-color: #373a44;
      }
    }
    &:hover {
      background-color: #373a44;
    }
  }
  .my-saved-reports-container {
    td {
      word-break: break-all;
    }
    .action-icon {
      margin-right: 10px;
    }
    .headers {
      color: white;
      vertical-align: top !important;
    }
    th {
      &:nth-child(1) {
        width: 20%;
      }
      &:nth-child(2) {
        width: 7%;
      }
      &:nth-child(3) {
        width: 10%;
      }
      &:nth-child(4) {
        width: 13%;
      }
    }
    tr td {
      &:nth-child(1) {
        width: 20%;
      }
      &:nth-child(2) {
        width: 7%;
      }
      &:nth-child(3) {
        width: 10%;
      }
      &:nth-child(4) {
        width: 13%;
      }
    }
    .action-icon {
      cursor: pointer;
    }
  }
  .my-scheduled-reports-container {
    td {
      word-break: break-all;
    }
    .action-icon {
      margin-right: 10px;
    }
    .headers {
      color: white;
      vertical-align: top !important;
    }
    th {
      &:nth-child(1) {
        width: 27%;
      }
      &:nth-child(2) {
        width: 7%;
      }
      &:nth-child(3) {
        width: 7%;
      }
      &:nth-child(4) {
        width: 9%;
      }
    }
    tr td {
      &:nth-child(1) {
        width: 27%;
      }
      &:nth-child(2) {
        width: 7%;
      }
      &:nth-child(3) {
        width: 7%;
      }
      &:nth-child(4) {
        width: 9%;
      }
    }
    .action-icon {
      cursor: pointer;
    }
  }
  .my-scheduled-reports-container {
    td {
      word-break: break-all;
    }
    .action-icon {
      margin-right: 10px;
    }
    .headers {
      color: white;
      vertical-align: top !important;
    }
    th {
      &:nth-child(1) {
        width: 20%;
      }
      &:nth-child(2) {
        width: 7%;
      }
      &:nth-child(3) {
        width: 7%;
      }
      &:nth-child(4) {
        width: 7%;
      }
      &:nth-child(5) {
        width: 9%;
      }
    }
    tr td {
      &:nth-child(1) {
        width: 20%;
      }
      &:nth-child(2) {
        width: 7%;
      }
      &:nth-child(3) {
        width: 7%;
      }
      &:nth-child(4) {
        width: 7%;
      }
      &:nth-child(5) {
        width: 9%;
      }
    }
  }
  .txt-center {
    text-align: center;
    width: 50%;
  }
  .txt-center-full {
    text-align: center;
    width: 100%;
  }
  .ftop-icons {
    position: relative;
    display: inline-block;
    vertical-align: top;
    color: var(--primarydark2);
    cursor: pointer;
    font-size: 16px;
    margin-right: 2px;
    margin-left: 2px;

    .ttip-wrap {
      position: absolute;
      width: max-content;
      text-align: center;
      transform: translate3d(0px, 0px, 0px);
      padding-top: 0px;
      display: inline-block;
      visibility: hidden;
      opacity: 0;
      font-size: 14px;
      transition: all 0.2s ease;
      z-index: 103;
      pointer-events: none;
      left: -47px;
      top: 20px;

      .ttip {
        display: inline-block;
        position: relative;
        padding: 4px 8px;
        border-radius: 6px;
        background-color: var(--primarydark2);
        color: #fff;
        text-align: center;
        font-size: 0.8em;
        font-weight: 500;
        line-height: 1.1em;
        margin-top: 0px;
      }
      .ttip::before {
        content: '';
        display: inline-block;
        left: 50%;
        top: -5px;
        height: 0px;
        width: 0px;
        border-right: solid 6px transparent;
        border-left: solid 6px transparent;
        border-bottom: solid 6px var(--primarydark2);
        position: absolute;
        margin-left: -3px;
      }
    }
    &:hover .fa-filter {
      color: var(--primarycolor);
    }
    &:hover .ttip-wrap {
      visibility: visible;
      opacity: 1;
      transform: translate3d(0px, 6px, 0px);
    }
  }
  .ftop-icons.disabled {
    pointer-events: auto;
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.saved-reports-cell {
  word-wrap: break-word; /* Allow long words to be broken and wrap onto the next line */
  overflow: hidden; /* Hide overflow content */
  white-space: normal; /* Allow text to wrap */
}

.parent-wrap {
  display: flex;
  gap: 15px;
}

.header-container {
  display: flex;
  flex-direction: column;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header-top {
  border-radius: 5px;
}

.filter-input {
  width: 100%;
  margin-top: 0.5rem;
  box-sizing: border-box;
  padding: 6px 10px;
  font-size: 12px;
  border-radius: 5px;
}

.filter-icon {
  transition: transform 0.3s ease;
}

.filter-icon.rotated {
  transform: rotate(90deg);
}
</style>
